// Import the Tippy.js library (installed via npm)
import tippy from 'tippy.js'

// Extend HTMLElement to recognize the internal _tippy instance used by Tippy
interface TippyElement extends HTMLElement {
  _tippy?: any
}

/**
 * Parses data-tippy-* attributes into a config object for Tippy.js.
 * Supports JSON values (like arrays or booleans) for options like delay, arrow, etc.
 */
function parseTippyOptions(el: HTMLElement): Record<string, any> {
  const options: Record<string, any> = {}

  for (const attr of el.attributes) {
    if (!attr.name.startsWith('data-tippy-')) continue

    const key = attr.name.replace('data-tippy-', '')

    if (key === 'content') continue // Handled separately

    // Try to parse the value as JSON (e.g., for arrays, booleans, numbers)
    try {
      options[key] = JSON.parse(attr.value)
    } catch {
      options[key] = attr.value // Fallback to plain string
    }
  }

  // Always set the tooltip content separately (guaranteed to be a string)
  options.content = el.getAttribute('data-tippy-content')
  return options
}

/**
 * Initializes Tippy tooltips on all matching elements.
 * Skips any element that already has a _tippy instance attached.
 */
export function initTippy(selector = '[data-tippy-content]') {
  const els = document.querySelectorAll<HTMLElement>(selector)

  for (const el of els) {
    const tipEl = el as TippyElement
    if (tipEl._tippy) continue // Skip if tooltip already initialized

    const options = parseTippyOptions(tipEl)
    tippy(tipEl, options) // Create tooltip instance
  }
}
/**
 * Auto-initialize tooltips on page load and after HTMX swaps.
 * This ensures tooltips work with dynamically loaded content.
 */
if (typeof window !== 'undefined') {
  document.addEventListener('DOMContentLoaded', () => initTippy())
  document.body.addEventListener('htmx:afterSwap', () => initTippy())
}
